import { formatGroupLifeDocument } from '@/features/quotes/utils/group-life/format';
import type { GroupLifeFrontSubStipulator } from '@/services/group-life';
import { Accordion, Text } from '@noorden-insurtech/alba-design-system';
import { ChevronDown } from 'lucide-react';
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import {
  mapSubStipulatorValues,
  type PmeStipulantValues,
} from '../../utils/hydrate-proposal.util';
import {
  PmeStipulantFormSection,
  type PmeStipulantFormSectionHandle,
} from './stipulant-form-section.component';

/** Documento (para casar no back) + valores do formulário de um subestipulante. */
export type PmeSubStipulantResult = {
  documentNumber: string;
  values: PmeStipulantValues;
};

export type PmeSubStipulantsSectionHandle = {
  validateRequired: () => Promise<boolean>;
  getValues: () => PmeSubStipulantResult[];
};

type Props = {
  subStipulators?: GroupLifeFrontSubStipulator[];
  isViewMode?: boolean;
};

/**
 * Bloco "Dados dos Subestipulantes" da proposta PME — um accordion por sub
 * (endereço + contato, e Sexo/Data quando CAEPF), no mesmo shape do estipulante
 * e no mesmo padrão do Capital Global.
 */
export const PmeSubStipulantsSection = forwardRef<
  PmeSubStipulantsSectionHandle,
  Props
>(function PmeSubStipulantsSection({ subStipulators, isViewMode = false }, ref) {
  const subs = useMemo(() => subStipulators ?? [], [subStipulators]);
  const refs = useRef<Array<PmeStipulantFormSectionHandle | null>>([]);

  // Começa fechado; se o "Avançar" reprovar algum sub, ele abre sozinho — erro
  // dentro de accordion fechado é erro invisível.
  const [openValues, setOpenValues] = useState<string[]>([]);

  const rows = useMemo(
    () =>
      subs.map((sub, index) => ({
        sub,
        values: mapSubStipulatorValues(sub),
        itemValue: `pme-sub-${sub.id ?? index}`,
      })),
    [subs],
  );

  useImperativeHandle(
    ref,
    () => ({
      validateRequired: async () => {
        const results = await Promise.all(
          refs.current.map(r => r?.validateRequired() ?? Promise.resolve(true)),
        );
        const invalid = rows
          .filter((_row, index) => results[index] === false)
          .map(row => row.itemValue);
        if (invalid.length > 0) {
          setOpenValues(current =>
            Array.from(new Set([...current, ...invalid])),
          );
        }
        return results.every(Boolean);
      },
      getValues: () =>
        subs.map((sub, index) => ({
          documentNumber: sub.cnpj,
          values:
            refs.current[index]?.getValues() ?? mapSubStipulatorValues(sub),
        })),
    }),
    [rows, subs],
  );

  if (subs.length === 0) return null;

  return (
    <div className='flex flex-col gap-6'>
      <Text className='font-montserrat text-base font-normal text-alba-black-500'>
        Dados dos Subestipulantes
      </Text>

      <Accordion.Root
        collapsible
        multiple
        value={openValues}
        onValueChange={details => setOpenValues(details.value)}
      >
        {rows.map(({ sub, values, itemValue }, index) => (
          <Accordion.Item
            key={itemValue}
            value={itemValue}
            className='mb-4 rounded-2xl border border-alba-gray-200 bg-white shadow-none'
          >
            {/* Só a identificação fica sempre visível; o resto colapsa. */}
            <Accordion.ItemTrigger className='flex w-full items-center justify-between gap-4 px-5 py-4 text-left'>
              <div className='flex flex-col gap-1'>
                <Text className='font-montserrat text-base font-semibold text-alba-black-500'>
                  {`Subestipulante ${index + 1} — ${sub.legalName || 'Sem razão social'}`}
                </Text>
                {sub.cnpj ? (
                  <Text className='font-montserrat text-sm font-normal text-alba-black-300'>
                    {formatGroupLifeDocument(sub.cnpj, !!sub.isCaepf)}
                  </Text>
                ) : null}
              </div>
              <Accordion.ItemIndicator>
                <ChevronDown className='h-4 w-4' strokeWidth={1.6} />
              </Accordion.ItemIndicator>
            </Accordion.ItemTrigger>

            <Accordion.ItemContent className='border-t border-alba-gray-200'>
              <div className='px-5 py-4'>
                <PmeStipulantFormSection
                  ref={el => {
                    refs.current[index] = el;
                  }}
                  title=''
                  showContact
                  values={values}
                  requireAddressFields
                  isViewMode={isViewMode}
                />
              </div>
            </Accordion.ItemContent>
          </Accordion.Item>
        ))}
      </Accordion.Root>
    </div>
  );
});
